<script setup>
import { ref, onMounted } from 'vue';
import gsap from 'gsap';
import GermanyMap from '../../CustomIcons/GermanyMap.vue';
import { useScrollAnimations } from '@/composables/useScrollAnimations';
import LandingCtaButton from './LandingCtaButton.vue';

const attacks = [
  { city: 'Berlin', date: '14.01.2025' },
  { city: 'Hamburg', date: '12.01.2025' },
  { city: 'München', mapCity: 'Bayern', date: '10.01.2025' },
  { city: 'Köln', mapCity: 'Nordrhein-Westfalen', date: '08.01.2025' },
  { city: 'Frankfurt', mapCity: 'Hessen', date: '05.01.2025' },
];

const currentIndex = ref(0);
const activeCity = ref(attacks[0].mapCity || attacks[0].city);
const visitedCities = ref([activeCity.value]);
const textContainer = ref(null);
const headingText = ref(attacks[0].city);
const dateText = ref(attacks[0].date);

// Animation Refs
const displayPercentage = ref(0);
const displaySmeCount = ref(0);
const heroHeadline = ref(null);
const heroSubtext = ref(null);
const heroCta = ref(null);
const statsHeadline = ref(null);
const statsSubtext = ref(null);
const statsMap = ref(null);

const { slideUp, slideLeft, slideRight, countUp, animateInView } = useScrollAnimations();

const animateText = () => {
  const tl = gsap.timeline({
    onComplete: () => {
      animateText();
    },
  });

  // Delay before fading out the current text
  tl.to(textContainer.value, {
    opacity: 0,
    duration: 0.5,
    delay: 2,
  });

  // Update text and map state while invisible
  tl.call(() => {
    currentIndex.value = (currentIndex.value + 1) % attacks.length;
    const nextAttack = attacks[currentIndex.value];

    // Update state for next cycle
    activeCity.value = nextAttack.mapCity || nextAttack.city;
    if (!visitedCities.value.includes(activeCity.value)) {
      visitedCities.value.push(activeCity.value);
    }
    headingText.value = nextAttack.city;
    dateText.value = nextAttack.date;
  });

  // Fade in new text
  tl.to(textContainer.value, {
    opacity: 1,
    duration: 0.5,
  });
};

const initScrollAnimations = () => {
  const heroSection = document.querySelector('#hero-section');
  const statsSection = document.querySelector('#stats-section');

  if (heroSection) {
    animateInView(heroSection, () => {
      slideUp(heroHeadline.value);
      slideUp(heroSubtext.value, 0.2);
      slideUp(heroCta.value, 0.4);
      countUp(displayPercentage, 95);
    });
  }

  if (statsSection) {
    animateInView(statsSection, () => {
      slideLeft(statsHeadline.value);
      slideLeft(statsSubtext.value, 0.2);
      slideRight(statsMap.value, 0.4);
      countUp(displaySmeCount, 170, 2.5);
    });
  }
};

onMounted(() => {
  animateText();
  initScrollAnimations();
});
</script>
<template>
  <div>
    <div class="relative isolate px-6  lg:px-8" id="hero-section">
      <div class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl hidden md:block"
        aria-hidden="true" />
      <div class="mx-auto max-w-5xl py-32 sm:py-48 lg:py-56">
        <div class="text-center">
          <h1 ref="heroHeadline"
            class="text-5xl font-semibold tracking-tight text-balance text-white sm:text-7xl w-full opacity-0"
            v-html="$t('landingPage.v2.hero.headline', { percentage: displayPercentage.toFixed(0) })">
          </h1>
          <p ref="heroSubtext" class="mt-8 text-lg font-medium text-pretty text-gray-400 sm:text-xl/8 opacity-0">
            {{ $t('landingPage.v2.hero.subtext') }}
          </p>
          <div ref="heroCta" class="mt-10 flex items-center justify-center gap-x-6 opacity-0">
            <LandingCtaButton sectionName="Hero" :text="$t('landingPage.v2.cta.hero')" />
          </div>
        </div>

        <div class="mt-20 grid grid-cols-1 md:grid-cols-3 gap-12 items-center" id="stats-section">
          <div class="text-left md:col-span-1">
            <h2 ref="statsHeadline"
              class="text-3xl font-bold tracking-tight text-white sm:text-4xl text-pretty opacity-0"
              v-html="$t('landingPage.v2.hero.statsHeadline', { count: displaySmeCount.toFixed(0) })">
            </h2>
            <p ref="statsSubtext" class="text-lg font-medium text-pretty text-gray-400 ">
              {{ $t('landingPage.v2.hero.statsSubtext') }}
            </p>
            <div ref="statsSubtext" class="opacity-0">
              <p class="mt-4 text-xl text-gray-400 " ref="textContainer">
                {{ dateText }}, {{ headingText }}
              </p>
            </div>
          </div>
          <div ref="statsMap" class="flex justify-end md:col-span-2 opacity-0">
            <div
              class="absolute inset-0 bg-indigo-500/10 blur-[100px] rounded-full pointer-events-none hidden md:block">
            </div>
            <GermanyMap class="w-full h-auto drop-shadow-lg" :active-city="activeCity"
              :visited-cities="visitedCities" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
